import { resolve, Promise } from 'rsvp';
import { isArray, A } from '@ember/array';
import { assert } from '@ember/debug';
import { underscore, camelize } from '@ember/string';
import { isEmpty, isPresent, isNone, isBlank, typeOf } from '@ember/utils';
import Service, { inject as service } from '@ember/service';
import { set } from '@ember/object';
import { later } from '@ember/runloop';
import { jsonPost, uploadMultipart } from '@square/dashboard-utilities/utils/xhr';
import Enum from '@square/dashboard-utilities/utils/enum';
import { deserializeCatalogAttributeValue, formatObjectAsCatalogResponse } from '@square/dashboard-items-data/utils/catalog-wire-format';
import CatalogError, { CatalogErrorType } from '@square/dashboard-data/utils/catalog/catalog-error';
import { ASYNC_SERVICE_STATUS_CODES, CATALOG_SERVICE_META_ATTRIBUTES, CATALOG_SERVICE_ATTRIBUTE_DEFINITIONS, CATALOG_SERVICE_ITEM_OPTION_ATTRIBUTE_TOKENS, CATALOG_SERVICE_ITEM_OPTION_VAL_ATTRIBUTE_TOKENS, CATALOG_ATTR_TYPES, CATALOG_SERVICE_TOKEN_TO_ATTRIBUTE_DEF_MAP, CATALOG_SERVICE_TYPES, CATALOG_SERVICE_QUERY_TYPES, CATALOG_SERVICE_TYPES_INVERSE, TRUE_ONLY_ATTRIBUTES, ITEMS_BULK_FAKE_ATTRIBUTES, CATALOG_SERVICE_RESOURCE_TOKEN, CATALOG_MODEL_NAMES, UNIT_OVERRIDE_ONLY_ATTRIBUTES } from '@square/dashboard-data/models/catalog/catalog-service-constants';
import { ItemTypesForFiltering_NumericStrings } from '@square/dashboard-items-data/utils/item-type-constants';
import { CatalogItemProductType } from '@square/dashboard-protos/models/squareup/connect/v2/catalog/resources/enums';
import Logger from '@square/dashboard-utilities/utils/logger';
import UUID from 'uuid-js';
import moment from 'moment';
import { sendErrorToSentry } from '@square/dashboard-items-data/utils/sentry';
export const URL_GET = '/api/v3/itemsfe/get';
const URL_PUT = '/api/v3/itemsfe/put';
const URL_COUNT = '/api/v3/items/count';
const URL_GET_COGS_ID = '/api/v3/items/get-cogs-id';
const URL_GET_MERCHANT_CATALOG_IDS = '/api/v3/items/get-merchant-catalog-tokens';
const URL_CLONE_UNIT = '/api/v3/items/cloneunit';
const URL_BATCH = '/api/v3/items/process-batch';
const URL_SEARCH_CATALOG = '/api/v3/itemsfe/search';
const URL_MEMBERSHIP_UPDATE_START = '/api/v3/itemsfe/membership-update-start';
const URL_ASYNC_SERVICE_STATUS = '/api/v3/itemsfe/async-status';
const URL_IMPORT_TEMPLATE = '/api/v3/items-bulk/import-template.csv';
const URL_IMPORT_UPLOAD_FILE = '/api/v3/items-bulk/upload-import-file';
const URL_IMPORT_START_ASYNC = '/api/v3/items-bulk/import-start-async';
const URL_GET_MERCHANT_IMPORTS = '/api/v3/items/get-merchant-imports';
const URL_ROLLBACK = '/api/v3/items/rollback';
const ONE_PERCENT = 100000; // from https://git.sqcorp.co/projects/GO/repos/square/browse/items/model/merchant/attribute_definition.go,
// SquarePercent defines a percentage.  It is actually represented in parts per 10,000,000.  In
// that way, 10% is expressed as 1,000,000, etc.
// therefore, to get our percent as a value between 1 and 100, we divide by 100000

const DEFAULT_SEARCH_ORDER = 'ASC';
export const PAGE_SIZE = 100;
export const TEST_PAGE_SIZE = 40;
export const INPUT_DEBOUNCE_INTERVAL_TIME = 300;
export const NULLABLE_SEARCH_CATALOG_FIELDS = new Enum(['VENDOR_TOKEN']);

// eslint-disable-next-line ember/no-classic-classes
export default Service.extend({
  store: service(),
  userInfo: service(),
  userWebPermissions: service(),
  features: service(),
  intl: service(),
  environment: service(),
  userSubscriptions: service(),
  catalogVersion: null,
  serializeForRpc: false,
  unloadAllCatalogRecords() {
    CATALOG_MODEL_NAMES.forEach(modelName => {
      this.unloadAllCatalogRecordsOfType(modelName);
    });
  },
  unloadAllCatalogRecordsOfType(modelName) {
    this.store.unloadAll(modelName);
    this.store.adapterFor(modelName).hasFetchedAll = false;
  },
  setSerializeForRpc(val) {
    set(this, 'serializeForRpc', val);
  },
  // matched_variations in the search response is a map from item tokens to lists of variation tokens that matched
  // the sent filters. An item token not appearing in the map indicates that all the item or all variations matched the filter.
  //
  // See squareup.items.merchant.DashboardSearchCatalogResponse proto for further response details.
  searchCatalog(params) {
    params = Object.assign({
      pageSize: PAGE_SIZE,
      sortOrder: DEFAULT_SEARCH_ORDER
    }, params);
    const request = {
      text_filter: params.text,
      category_token: params.categories,
      stock_level: params.stockLevels,
      product_types: params.productTypes,
      enabled_unit_tokens: params.unitTokens,
      vendor_token: params.vendorTokens,
      pagination_token: params.paginationToken,
      limit: params.pageSize,
      sort_order: params.sortOrder,
      does_not_exist: params.nullableFields,
      search_vendor_code: params.searchVendorCode,
      archived_state: params.archivedState
    };
    return jsonPostWithErrorWrapper(URL_SEARCH_CATALOG, request).then(response => {
      let matchedVariations = null;
      if (isPresent(response.matched_variations)) {
        matchedVariations = {};
        Object.keys(response.matched_variations).forEach(itemToken => {
          matchedVariations[itemToken] = response.matched_variations[itemToken].variation_token;
        });
      }
      return {
        objects: this._getCatalogObjectsFromResponse(response),
        paginationToken: response.pagination_token,
        matchedVariations
      };
    });
  },
  metaAttributeNames() {
    return CATALOG_SERVICE_META_ATTRIBUTES;
  },
  attributeDefinitionToken(attributeName) {
    return CATALOG_SERVICE_ATTRIBUTE_DEFINITIONS[attributeName].token;
  },
  membershipUpdateStart(membershipType, targetObjectToken, selectAll, exceptionItemTokens, taxOptions = null) {
    // These do not reference ItemFeeMembership.catalogType() or ItemItemModifierListMembership.catalogType()
    // to avoid an import cycle. CatalogService shouldn't have a dependency on the application level models.
    // These constants could be in catalog-service-constants.js, but for simplicity they're used inline.
    if (membershipType !== 'ITEM_FEE_MEMBERSHIP' && membershipType !== 'ITEM_ITEM_MODIFIER_LIST_MEMBERSHIP') {
      throw new Error('CatalogService.membershipUpdateStart unsupported membershipType');
    }
    if (isEmpty(targetObjectToken) || typeof targetObjectToken !== 'string') {
      throw new Error('CatalogService.membershipUpdateStart targetObjectToken is required');
    }
    if (typeof selectAll !== 'boolean') {
      throw new TypeError('CatalogService.membershipUpdateStart selectAll is required');
    }
    const params = {};
    params.membership_type = CATALOG_SERVICE_TYPES[membershipType];
    params.target_object_token = targetObjectToken;
    params.select_all = selectAll;
    if (isPresent(exceptionItemTokens)) {
      if (!isArray(exceptionItemTokens)) {
        throw new Error('CatalogService.membershipUpdateStart exceptionItemTokens must be an array');
      }
      params.exception_item_tokens = exceptionItemTokens;
    }
    if (isPresent(taxOptions)) {
      const tax_options = {};
      const {
        createMatchAllProductSet
      } = taxOptions;
      if (typeof createMatchAllProductSet === 'boolean') {
        tax_options.create_match_all_product_set = createMatchAllProductSet;
      }
      if (isPresent(Object.keys(tax_options))) {
        params.tax_options = tax_options;
      }
    }
    return jsonPostWithErrorWrapper(URL_MEMBERSHIP_UPDATE_START, params);
  },
  lastImportStatus: null,
  lastImportToken: null,
  setLastImportStatus(importStatus) {
    this.set('lastImportStatus', importStatus);
  },
  clearImportStatus() {
    this.set('lastImportStatus', null);
    this.set('lastimportToken', null);
  },
  getLastImportStatus() {
    return this.lastImportStatus;
  },
  getLastImportToken() {
    return this.lastImportToken;
  },
  importInProgress() {
    const status = this.lastImportStatus;
    return isPresent(status) && ASYNC_SERVICE_STATUS_CODES[status.status_code] === ASYNC_SERVICE_STATUS_CODES.IN_PROGRESS;
  },
  getAsyncServiceStatus(statusToken) {
    const statusRequest = {
      status_token: statusToken
    };
    return jsonPostWithErrorWrapper(URL_ASYNC_SERVICE_STATUS, statusRequest);
  },
  importTemplateURL() {
    return URL_IMPORT_TEMPLATE;
  },
  uploadImportFile(file, overwrite, returnRows = 10, fileType = 'XLSX', mode = 'NORMAL') {
    const request = JSON.stringify({
      lang_code: this.userInfo.browserLanguage,
      overwrite,
      return_rows: returnRows,
      file_type: fileType,
      mode
    });
    const fd = new FormData();
    fd.append('file', file);
    fd.append('request', request);
    return uploadMultipartWithErrorWrapper(URL_IMPORT_UPLOAD_FILE, fd);
  },
  getMerchantImports() {
    return jsonPostWithErrorWrapper(URL_GET_MERCHANT_IMPORTS, {
      merchant_token: this.userInfo.merchantToken
    });
  },
  async _pollForAsyncUpdateStatusForRollback(asyncStatusToken, selectedImport, afterRollback) {
    const asyncStatus = await this.getAsyncServiceStatus(asyncStatusToken);
    this.setLastImportStatus(asyncStatus);
    const importId = selectedImport.requestToken;
    const importTimestamp = selectedImport.importTimestamp;
    switch (ASYNC_SERVICE_STATUS_CODES[asyncStatus.status_code]) {
      case ASYNC_SERVICE_STATUS_CODES.IN_PROGRESS:
      case ASYNC_SERVICE_STATUS_CODES.CANCEL_REQUESTED:
        return later(this, '_pollForAsyncUpdateStatusForRollback', asyncStatusToken, selectedImport, afterRollback, asyncStatus.poll_delay_ms);
      case ASYNC_SERVICE_STATUS_CODES.CANCELLED:
        this.clearImportStatus();
        afterRollback({
          warningMessage: this.intl.t('items.itemLibrary.undoCatalog.undoCatalogCancelled'),
          importId,
          importTimestamp,
          timing: moment().diff(this.rollbackStartTime)
        });
        this.set('rollbackStartTime', null);
        break;
      case ASYNC_SERVICE_STATUS_CODES.SUCCESS:
        this.clearImportStatus();
        afterRollback({
          importId,
          importTimestamp,
          timing: moment().diff(this.rollbackStartTime)
        });
        this.set('rollbackStartTime', null);
        break;
      case ASYNC_SERVICE_STATUS_CODES.ERROR:
        this.clearImportStatus();
        afterRollback({
          errorMessage: this.intl.t('common.messages.error'),
          importId,
          importTimestamp,
          timing: moment().diff(this.rollbackStartTime)
        });
        this.set('rollbackStartTime', null);
        break;
      case ASYNC_SERVICE_STATUS_CODES.TIMED_OUT:
        this.clearImportStatus();
        afterRollback({
          errorMessage: this.intl.t('items.itemLibrary.undoCatalog.undoCatalogTimeOut'),
          importId,
          importTimestamp,
          timing: moment().diff(this.rollbackStartTime)
        });
        this.set('rollbackStartTime', null);
        break;
      default:
        this.clearImportStatus();
        afterRollback({
          errorMessage: this.intl.t('items.itemLibrary.import.connectionError'),
          importId,
          importTimestamp,
          timing: moment().diff(this.rollbackStartTime)
        });
        this.set('rollbackStartTime', null);
        break;
    }
    return asyncStatus;
  },
  async rollbackCatalog(selectedImport, afterRollback) {
    this.set('rollbackStartTime', moment());
    try {
      const response = await jsonPostWithErrorWrapper(URL_ROLLBACK, {
        merchant_token: this.userInfo.merchantToken,
        request_token: selectedImport.requestToken
      });
      return this._pollForAsyncUpdateStatusForRollback(response.status_token, selectedImport, afterRollback);
    } catch (catalogError) {
      return afterRollback({
        importId: selectedImport.requestToken,
        errorMessage: catalogError,
        timing: moment().diff(this.rollbackStartTime)
      });
    }
  },
  importProductsStart(file, fileToken, fileType, headerBindings, currencyBase, overwrite, mode = 'Normal', acceptAutoResolve = false) {
    if (!isNone(file) && !isNone(fileToken)) {
      throw new Error('CatalogService.importProductsStart pass a file or fileToken, but not both');
    }
    if (isNone(file) && isNone(fileToken)) {
      throw new Error('CatalogService.importProductsStart a file or fileToken is required');
    }
    const request = {
      lang_code: this.userInfo.browserLanguage,
      bindings: headerBindings,
      currency_base: currencyBase,
      file_type: fileType,
      overwrite,
      mode,
      accept_auto_resolve: acceptAutoResolve
    };
    if (!isNone(file)) {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('request', JSON.stringify(request));
      if (this.userWebPermissions.canIgnoreItemsBulkObjectLimit) {
        fd.append('ignore_items_limit', 'true');
      }
      return uploadMultipartWithErrorWrapper(URL_IMPORT_START_ASYNC, fd);
    } else {
      request.file_token = fileToken;
      return jsonPostWithErrorWrapper(URL_IMPORT_START_ASYNC, request);
    }
  },
  _buildPutRequest(objectList, {
    convertToCatalogObject = true,
    ignoreItemDefaults
  } = {}) {
    const putRequest = {
      catalog_object: [],
      idempotency_key: UUID.create().toString()
    };
    if (ignoreItemDefaults) {
      putRequest.ignore_item_defaults = ignoreItemDefaults;
    }
    objectList.forEach(object => {
      const catalogObject = convertToCatalogObject ? this._toCatalogObject(object) : object;
      putRequest.catalog_object.push(catalogObject);
    });
    return putRequest;
  },
  _updateCatalogVersion(version) {
    if (version > this.catalogVersion) {
      this.set('catalogVersion', version);
    }
  },
  // Simple helper method for performing a basic get request to Catalog API
  _getRequest(params, handler) {
    return jsonPostWithErrorWrapper(URL_GET, params).then(response => {
      this._updateCatalogVersion(response.catalog_version);
      return handler.call(this, response);
    });
  },
  // Simple helper method for performing a basic put request to Catalog API
  _putRequest(params, handler) {
    return jsonPostWithErrorWrapper(URL_PUT, params).then(response => {
      this._updateCatalogVersion(response.modification_timestamp);
      return handler.call(this, response);
    });
  },
  // Helper method for performing a chain of get requests to Catalog API
  // and returning the collected objects from all requests in the chain
  _getRequestChainObjects(params, objects = []) {
    return this._getRequest(params, response => {
      objects.push(...this._getCatalogObjectsFromResponse(response));
      if (!isNone(response.pagination_token) && response.pagination_token.length > 0) {
        params.pagination_token = response.pagination_token;
        return this._getRequestChainObjects(params, objects);
      } else {
        return objects;
      }
    });
  },
  // Helper method for retrieving Catalog objects from the Catalog API.
  // If paginated is true, then this method will perform a single request
  // and return the objects for that first request.
  // If paginated is false, then this method will perform a chain of
  // requests until all pages have been retrieved and return the
  // resulting collected objects from the chain
  _getRequestObjects(params, paginated = false) {
    if (paginated) {
      return this._getRequest(params, response => {
        const objects = this._getCatalogObjectsFromResponse(response);
        objects.paginationToken = response.pagination_token;
        return objects;
      });
    } else {
      return this._getRequestChainObjects(params);
    }
  },
  // https://prototype.sqprod.co/#/docs/squareup.items.merchant.CountRequest
  // get the count of objects of a particular set of types that are using a custom
  // attribute definition. Currently only supports a few parameters used from the
  // custom attributes page.
  getCountForCustomAttribute({
    types,
    customAttributeDefinition,
    limit = 1000
  }) {
    const params = {};
    params.types = [...new Set(types.map(type => CATALOG_SERVICE_TYPES[type]))];
    params.attribute_definition_token = customAttributeDefinition.id;
    params.limit = limit;
    return jsonPostWithErrorWrapper(URL_COUNT, params).then(response => {
      if (response.error_code) {
        throw new Error(`CatalogService.getCount error_code: ${response.error_code}`);
      } else {
        return response.count;
      }
    });
  },
  /*
    proto: https://prototype.sqprod.co/#/docs/squareup.items.merchant.CountRequest
    Example: how to get the count of Items in a Category
    types = a list of Catalog types ([Item.catalogType()])
    filterAttribute = an attribute to filter the items you count ('menu_category_id')
    filterAttributes = an attributes to filter the items you count (['menu_category_id', additional_categories])
    filterStringValue = the string value for the filterAttribute above (a category id)
    filterIntValues = array of int values for the filterAttribute above (e.g. filterAttribute = item type, filterIntValues = [0,6,10])
    additionalFilterAttribute = a second filter attribute for int/bool values (not used in this example)
    additionalFilterIntValue = the int value for the additionalFilterAttribute above
    OR
    additionalFilterIntValueExclude = the int value to exclude for the additionalFilterAttribute ("count all objects except the ones that have this value")
    limit = when to stop counting, server imposes a default max of 1000
    modifierAfter = only count objects modifier after a date
    enabledUnitTokens = If present, only count objects that are enabled at any of these locations or global enabled
  */
  // eslint-disable-next-line complexity
  getCount({
    types = [],
    filterAttribute,
    filterAttributes,
    filterStringValue,
    filterIntValues,
    additionalFilterAttribute,
    additionalFilterIntValue,
    additionalFilterIntValueExclude,
    limit = 1000,
    modifiedAfter,
    enabledUnitTokens
  }) {
    if (!isArray(types)) {
      throw new Error('CatalogService.getCount types must be an array');
    }
    if (isPresent(additionalFilterAttribute)) {
      if (isPresent(additionalFilterIntValue) && isPresent(additionalFilterIntValueExclude)) {
        throw new Error('CatalogService.getCount cannot pass both a filter int value and exclude value');
      }
      if ((isNone(additionalFilterIntValue) || typeof additionalFilterIntValue !== 'number') && (isNone(additionalFilterIntValueExclude) || typeof additionalFilterIntValueExclude !== 'number')) {
        throw new Error('CatalogService.getCount if a additional filter attribute is passed, then a filter int value must also be supplied');
      }
    }
    const params = {};
    if (isPresent(types)) {
      params.types = [...new Set(types.map(type => CATALOG_SERVICE_TYPES[type]))];
    }
    if (isPresent(filterAttribute)) {
      const definition = CATALOG_SERVICE_ATTRIBUTE_DEFINITIONS[filterAttribute];
      if (!definition) {
        throw new Error('CatalogService.getCount unknown filter attribute');
      }
      params.attribute_definition_token = definition.token;
      if (isPresent(filterStringValue)) {
        params.attribute_string_value = filterStringValue;
      }
      if (isPresent(filterIntValues)) {
        if (!Array.isArray(filterIntValues)) {
          throw new TypeError('CatalogService.getCount filterIntValues should be an array of ints');
        }
        params.attribute_int_values = filterIntValues;
      }
    }
    if (isPresent(filterAttributes)) {
      const definitions = filterAttributes.map(attr => CATALOG_SERVICE_ATTRIBUTE_DEFINITIONS[attr]?.token).compact();
      params.attribute_definition_tokens = definitions;
      params.attribute_string_value = filterStringValue;
    }
    if (isPresent(additionalFilterAttribute)) {
      const definition = CATALOG_SERVICE_ATTRIBUTE_DEFINITIONS[additionalFilterAttribute];
      if (!definition) {
        throw new Error('CatalogService.getCount unknown filter attribute');
      }
      params.additional_attribute_definition_token = definition.token;
      if (!isNone(additionalFilterIntValue)) {
        params.additional_attribute_int_value = additionalFilterIntValue;
      }
      if (!isNone(additionalFilterIntValueExclude)) {
        params.additional_attribute_int_value_exclude = additionalFilterIntValueExclude;
      }
      if (isNone(filterAttribute) && isNone(filterAttributes)) {
        // The `count` API requires that `attribute_definition_token` is used before `additional_attribute_definition_token`, but
        // the `attribute_definition_token` cannot be used for int value attributes. If we don't have an initial string value attribute
        // to search but need to count by an int attribute we can achieve that by passing the int attribute definition as
        // `attribute_definition_token`. The server will treat this as a filter that the attribute exists without checking against
        // any values. The second part of the filter with `additional_attribute_definition_token` will verify the value.

        // We also check that there is no `attribute_definition_tokens` set, as the server constraint specifies that
        // we cannot supply both `attribute_definition_token` and `attributes_definition_token`.
        params.attribute_definition_token = definition.token;
      }
    }
    params.limit = limit;
    params.modified_after = modifiedAfter;
    if (isPresent(enabledUnitTokens)) {
      params.enabled_unit_tokens = enabledUnitTokens;
    }
    return jsonPostWithErrorWrapper(URL_COUNT, params).then(response => {
      if (response.error_code) {
        throw new Error(`CatalogService.getCount error_code: ${response.error_code}`);
      } else {
        return response.count;
      }
    });
  },
  /**
   *
   * @param {options.enabledUnitTokens} enabledUnitTokens If present, only count objects that are enabled at any of these locations
   * @param {options.productTypes} productTypes int array of item types to be filtered for
   * @returns True if any item exists that satisfy the filter parameters
   */
  hasAnyItems({
    enabledUnitTokens = undefined,
    productTypes
  }) {
    return this.getCount({
      types: ['ITEM'],
      filterAttribute: 'type',
      // filtering on item type
      filterIntValues: productTypes ?? [CatalogItemProductType.REGULAR],
      // defaulting to REGULAR type
      enabledUnitTokens,
      limit: 1 // we only need to check if there's at least one item
    }).then(count => {
      return count > 0;
    });
  },
  // getCogsIds maps from items v3 ids to v1 ids.
  // v3Ids: must be an array (can be empty) of only non-empty strings.
  // unitTokens [optional]: non-empty array of non-empty unit token strings.
  // returns: promise resolving to a hash of v3 id to array of v1 id strings.
  // note: v3Ids without COGS id translations will have no mapping returned.
  getCogsIds(v3Ids, unitTokens) {
    if (!isArray(v3Ids)) {
      throw new Error('v3Ids must be an array');
    }
    if (!v3Ids.every(id => typeof id === 'string' && id.length > 0)) {
      throw new Error('v3Ids must contain only non-empty strings');
    }
    if (!isNone(unitTokens)) {
      if (!isArray(unitTokens)) {
        throw new Error('when defined unitTokens must be an array');
      }
      if (!unitTokens.every(token => typeof token === 'string' && token.length > 0)) {
        throw new Error('when defined unitTokens must contain only non-empty strings');
      }

      // if unitTokens is an empty array, that should indicate that we should
      // not filter the output at all, as opposed to outputting nothing
      if (unitTokens.length === 0) {
        unitTokens = undefined;
      }
    }
    if (v3Ids.length === 0) {
      return resolve({});
    }
    const request = {
      token: v3Ids
    };
    if (!isEmpty(unitTokens)) {
      request.unit_token = unitTokens;
    }
    return jsonPostWithErrorWrapper(URL_GET_COGS_ID, request).then(response => {
      return response.id_mapping.reduce((hash, mapping) => {
        // TODO(mpa): remove once items deployed with object_token here
        // TODO(mpa): and remove "getCogsId compatibility with token field" test
        if (!mapping.object_token) {
          mapping.object_token = mapping.token;
        }
        if (!isNone(mapping.id)) {
          mapping.id.forEach(map => {
            const {
              object_id,
              unit_token
            } = map;
            if (isNone(unitTokens) || A(unitTokens).includes(unit_token)) {
              if (!hash[mapping.object_token]) {
                hash[mapping.object_token] = [];
              }
              hash[mapping.object_token].push(object_id);
            }
          });
        }
        return hash;
      }, {});
    });
  },
  // getMerchantCatalogTokens maps from items v1 ids to v3 ids.
  // v1Ids: must be an array (can be empty) of only non-empty v1 id strings.
  // returns: promise resolving to a hash of v1 di to array of v3 id strings.
  // note: v1Ids without a different v3 id will map back to their v1 id.
  getMerchantCatalogTokens(v1Ids) {
    if (!isArray(v1Ids)) {
      throw new Error('cogsIds must be an array');
    }
    if (!v1Ids.every(id => typeof id === 'string' && id.length > 0)) {
      throw new Error('v1Ids must contain only non-empty strings');
    }
    if (v1Ids.length === 0) {
      return resolve({});
    }
    const request = {
      cogs_id: v1Ids
    };
    return jsonPostWithErrorWrapper(URL_GET_MERCHANT_CATALOG_IDS, request).then(response => {
      return response.token_mapping.reduce((hash, mapping) => {
        const {
          cogs_id,
          object_token
        } = mapping;
        if (!hash[cogs_id]) {
          hash[cogs_id] = [];
        }
        if (object_token.length > 0) {
          hash[cogs_id].push(...object_token);
        } else {
          // echo back v1 id as v3 id when no mapping in response
          hash[cogs_id].push(cogs_id);
        }
        return hash;
      }, {});
    });
  },
  // Create a set of parameters for forming a Catalog Query in an iterative fashion
  // This method is useful when you need a more custom Catalog API query that doesn't
  // fit the existence convenience methods like getObjectsById, getObjectsByText, etc.
  //
  // Note that deleted objects returned may not have all their last attributes present
  // since server purges old deleted attributes.
  createObjectQuery({
    catalogTypes = [],
    paginationToken = null,
    sortKey = null,
    unitTokens = null,
    pageSize = PAGE_SIZE,
    skipCatalogTypeFilterForTypes = [],
    // TODO: delete with base-catalog-model/catalog-adapter
    modifiedAfter = null,
    includeDeleted = false,
    includeObjectsMissingAttribute = false
  } = {}) {
    const params = {
      query: []
    };
    if (catalogTypes.length > 0) {
      params.type = catalogTypes.map(type => CATALOG_SERVICE_TYPES[type]);
      catalogTypes.forEach(type => {
        const catalogTypeFilterQuery = this._getCatalogTypeFilterQuery(type);
        if (catalogTypeFilterQuery && !skipCatalogTypeFilterForTypes.includes(type)) {
          params.query.push(catalogTypeFilterQuery);
        }
      });
    }
    if (!isNone(paginationToken)) {
      params.pagination_token = paginationToken;
      params.limit = pageSize;
    }
    if (sortKey) {
      const attributeDefinition = CATALOG_SERVICE_ATTRIBUTE_DEFINITIONS[sortKey];
      if (attributeDefinition) {
        const token = attributeDefinition.token;
        if (token) {
          const query = {
            type: CATALOG_SERVICE_QUERY_TYPES.SORTED_ATTRIBUTE,
            key: token
          };
          if (includeObjectsMissingAttribute) {
            query.sorted_attribute = {
              include_objects_missing_attribute: includeObjectsMissingAttribute
            };
          }
          params.query.push(query);
        }
      }
    }
    if (isPresent(unitTokens)) {
      assert('unit tokens must be of type array', Array.isArray(unitTokens));
      params.enabled_unit_tokens = unitTokens;
    }
    if (isPresent(modifiedAfter)) {
      params.modified_after = modifiedAfter;
    }
    params.include_deleted = includeDeleted;
    return params;
  },
  // Augment a set of query parameters with a filter by tokens
  addFilterByTokens(params, tokens) {
    params.query.push(...tokens.map(token => {
      if (typeof token === 'object') {
        token = token.id;
      }
      return {
        type: CATALOG_SERVICE_QUERY_TYPES.TOKEN,
        text_term: token
      };
    }));
    return params;
  },
  // Augment a set of query parameters with a filter by an attribute
  addFilterByAttribute(params, attributeKey, filterValue) {
    const attributeDefinition = CATALOG_SERVICE_ATTRIBUTE_DEFINITIONS[attributeKey];
    if (attributeDefinition) {
      const token = attributeDefinition.token;
      if (token) {
        if (typeOf(filterValue) === 'string') {
          params.query.push({
            type: CATALOG_SERVICE_QUERY_TYPES.EXACT,
            key: token,
            text_term: filterValue
          });
        } else if (typeOf(filterValue) === 'array') {
          params.query.push({
            type: CATALOG_SERVICE_QUERY_TYPES.SET,
            key: token,
            set_query_values: filterValue
          });
        }
      }
    }
    return params;
  },
  addFilterByAttributeRange(params, attributeKey, minValue, maxValue) {
    const attributeDefinition = CATALOG_SERVICE_ATTRIBUTE_DEFINITIONS[attributeKey];
    if (attributeDefinition) {
      const token = attributeDefinition.token;
      if (token) {
        params.query.push({
          type: CATALOG_SERVICE_QUERY_TYPES.RANGE,
          key: token,
          range: {
            begin: minValue,
            end: maxValue
          }
        });
      }
    }
    return params;
  },
  // Augment a set of query parametes with a filter by any unit
  addFilterByAnyUnit(params, unitTokens) {
    params.enabled_unit_tokens = unitTokens;
    return params;
  },
  // Augment a set of query parameters with a filter by full text search
  addFilterByText(params, filterText) {
    params.query.push({
      type: CATALOG_SERVICE_QUERY_TYPES.TEXT,
      text_term: filterText
    });
    return params;
  },
  addFilterByStringIntAttributeValues(params, attributeValues) {
    const query = {
      type: CATALOG_SERVICE_QUERY_TYPES.STRING_INT_ATTRIBUTE_VALUES,
      attribute_values: {
        attribute_values: []
      }
    };
    attributeValues.forEach(attributeValue => {
      query.attribute_values.attribute_values.push({
        attribute_definition_tokens: attributeValue.attributeDefinitionTokens,
        string_int_value: {
          string_values: attributeValue.stringValues || [],
          int_value: attributeValue.intValue,
          include_null: attributeValue.includeNull || false
        }
      });
    });
    params.query.push(query);
    return params;
  },
  //  Augment a set of query parameters with a filter by attribute values
  addFilterByAttributeValues(params, attributeValues) {
    const attributeValuesQuery = {
      type: CATALOG_SERVICE_QUERY_TYPES.ATTRIBUTE_VALUES,
      attribute_values: {
        attribute_values: []
      }
    };
    attributeValues.forEach(attributeValue => {
      const attributeDefinitionTokens = attributeValue.attributeDefinitionTokens;
      if (isPresent(attributeValue.intValue)) {
        attributeValuesQuery.attribute_values.attribute_values.push({
          attribute_definition_tokens: attributeDefinitionTokens,
          int_value: attributeValue.intValue
        });
      } else if (isPresent(attributeValue.stringValue)) {
        attributeValuesQuery.attribute_values.attribute_values.push({
          attribute_definition_tokens: attributeDefinitionTokens,
          string_value: attributeValue.stringValue
        });
      } else {
        assert(`Must supply either intValue or stringValue inside attributeValue. This is the attributeValue you passed in: ${attributeValue}`);
      }
    });
    params.query.push(attributeValuesQuery);
  },
  // Execute a query for a set of objects
  runObjectQuery(params) {
    const paginated = !isNone(params.pagination_token);
    return this._getRequestObjects(params, paginated);
  },
  // Get an object by id
  getObjectById(catalogType, token) {
    if (isBlank(token)) {
      return resolve();
    }
    const params = this.createObjectQuery({
      catalogTypes: [catalogType],
      skipCatalogTypeFilterForTypes: [catalogType]
    });
    this.addFilterByTokens(params, [token]);
    return this.runObjectQuery(params).then(objects => objects.get('firstObject'));
  },
  // Get an object by type
  getObjectByType(catalogType) {
    const params = this.createObjectQuery({
      catalogTypes: [catalogType],
      skipCatalogTypeFilterForTypes: [catalogType]
    });
    return this._getRequest(params, response => {
      return response?.catalog_object?.get('firstObject');
    });
  },
  putObject(object, options) {
    const params = this._buildPutRequest([object], {
      convertToCatalogObject: false,
      ignoreItemDefaults: options?.ignoreItemDefaults
    });
    return this._putRequest(params, response => {
      return response?.catalog_object?.get('firstObject');
    });
  },
  // Get a set of objects by ids
  getObjectsById(tokens, {
    paginationToken = null,
    unitTokens = null,
    ...options
  } = {}) {
    if (tokens.length > 0) {
      const params = this.createObjectQuery({
        paginationToken,
        unitTokens,
        ...options
      });
      this.addFilterByTokens(params, tokens);
      return this.runObjectQuery(params);
    } else {
      return resolve([]);
    }
  },
  // Get a set of objects by full text search
  getObjectsByText(catalogTypes, text, sortKey = null, paginationToken = null, unitTokens = null, pageSize) {
    const params = this.createObjectQuery({
      catalogTypes,
      paginationToken,
      unitTokens,
      sortKey,
      pageSize
    });
    this.addFilterByText(params, text);
    return this.runObjectQuery(params);
  },
  // Get a set of objects
  getObjects({
    catalogTypes,
    sortKey = null,
    paginationToken = null,
    unitTokens = null,
    pageSize,
    skipCatalogTypeFilterForTypes = []
  }) {
    const params = this.createObjectQuery({
      catalogTypes,
      paginationToken,
      unitTokens,
      sortKey,
      pageSize,
      skipCatalogTypeFilterForTypes
    });
    return this.runObjectQuery(params);
  },
  // Method for retrieving hasManyTo related objects for a set of objects
  // tokens: an array of the tokens for the objects for which we are finding neighbors
  // neighborKeys: a hash keyed by catalog types where each value is the key that points
  //               back to an object whose token is in the tokens array
  // paginationToken: token for next page if performing a paginated query
  // sortKey: sort key
  getNeighborObjects(tokens, neighborKeys, sortKey = null, paginationToken = null) {
    const catalogTypes = Object.keys(neighborKeys);
    if (catalogTypes.length > 0) {
      const params = this.createObjectQuery({
        catalogTypes,
        paginationToken,
        sortKey,
        skipCatalogTypeFilterForTypes: catalogTypes
      });
      params.query.push(...tokens.map(token => {
        const subquery = {
          type: CATALOG_SERVICE_QUERY_TYPES.NEIGHBOR,
          neighbor_types: [],
          text_term: token
        };
        catalogTypes.forEach(catalogType => {
          const attributeKey = neighborKeys[catalogType];
          const attributeDefinition = CATALOG_SERVICE_ATTRIBUTE_DEFINITIONS[attributeKey];
          const neighbor = {
            object_type: CATALOG_SERVICE_TYPES[catalogType],
            attribute_definition_tokens: [attributeDefinition.token]
          };
          subquery.neighbor_types.push(neighbor);
        });
        return subquery;
      }));
      return this.runObjectQuery(params);
    } else {
      return resolve([]);
    }
  },
  _buildDeleteRequestFromTokens(tokens) {
    return {
      catalog_object_token: tokens
    };
  },
  // item option and value attributes are represented by up to 6 sequential attribute definitions (SQ_IO_X for
  // item options and SQ_IOV_X for item option values). Here we convert the id arrays into these attributes.
  _serializeItemOptionAttributes(catalogObject, object) {
    if (isPresent(object.item_option_ids)) {
      assert('Number of item options cannot exceed the number of item option attributes', object.item_option_ids.length <= CATALOG_SERVICE_ITEM_OPTION_ATTRIBUTE_TOKENS.length);
      object.item_option_ids.forEach((optionId, i) => {
        const attribute = {
          definition_token: CATALOG_SERVICE_ITEM_OPTION_ATTRIBUTE_TOKENS[i],
          string_value: optionId
        };
        catalogObject.attribute.push(attribute);
      });
    }
    if (isPresent(object.item_option_value_ids)) {
      assert('Number of item option values cannot exceed the number of item option value attributes', object.item_option_value_ids.length <= CATALOG_SERVICE_ITEM_OPTION_VAL_ATTRIBUTE_TOKENS.length);
      object.item_option_value_ids.forEach((optionValueId, i) => {
        const attribute = {
          definition_token: CATALOG_SERVICE_ITEM_OPTION_VAL_ATTRIBUTE_TOKENS[i],
          string_value: optionValueId
        };
        catalogObject.attribute.push(attribute);
      });
    }
  },
  _serializeResourceAttributes(catalogObject, object) {
    if (isPresent(object.resources)) {
      object.resources.forEach(membership => {
        const attribute = {
          definition_token: CATALOG_SERVICE_RESOURCE_TOKEN,
          string_value: membership.resource_id,
          unit_token: membership.unit_token
        };
        catalogObject.attribute.push(attribute);
      });
    }
  },
  // _serializeCatalogAttribute returns an object ready to be JSON-encoded
  // attributeValue may be null (never undefined) in which case the corresponding value field will be null
  // eslint-disable-next-line complexity
  _serializeCatalogAttribute(attributeName, attributeValue, unitToken) {
    if (attributeValue === undefined) {
      throw new Error('attributeValue must not be undefined');
    }
    if (attributeValue === null && isNone(unitToken)) {
      throw new Error('null attributes are only allowed for unit overrides');
    }
    if (typeof unitToken !== 'string' && unitToken !== undefined) {
      throw new Error('unitToken must be either a string or undefined');
    }
    const catalogType = CATALOG_SERVICE_ATTRIBUTE_DEFINITIONS[attributeName];
    if (isNone(catalogType)) {
      throw new Error(`CATALOG_SERVICE_ATTRIBUTE_DEFINITIONS has no mapping for: ${attributeName}`);
    }
    const attribute = {};
    attribute.definition_token = catalogType.token;
    if (unitToken) {
      attribute.unit_token = unitToken;
    }
    if (!isNone(attributeValue)) {
      switch (catalogType.type) {
        case CATALOG_ATTR_TYPES.STRING:
        case CATALOG_ATTR_TYPES.TEXT:
        case CATALOG_ATTR_TYPES.TOKEN:
          if (typeof attributeValue === 'string') {
            attribute.string_value = attributeValue;
          } else {
            throw new TypeError(`invalid attributeValue ${catalogType.type}: ${attributeValue}`);
          }
          break;
        case CATALOG_ATTR_TYPES.INT:
        case CATALOG_ATTR_TYPES.ENUM:
          if (typeof attributeValue === 'string') {
            // parseFloat allows us to detect strings with a fractional part and throw an error
            attributeValue = Number.parseFloat(attributeValue);
          }
          if (typeof attributeValue === 'number') {
            // eslint-disable-next-line unicorn/prefer-number-properties -- TODO: try switching to Number.isFinite
            if (!isFinite(attributeValue)) {
              throw new TypeError('attributeValue must be finite');
            }
            if (Math.floor(attributeValue) !== attributeValue) {
              throw new Error('attributeValue must be an integer');
            }
            if (attributeValue > Number.MAX_SAFE_INTEGER) {
              throw new Error('attributeValue is larger than Number.MAX_SAFE_INTEGER');
            }
            attribute.int_value = Number.parseInt(attributeValue, 10);
          } else {
            throw new TypeError(`invalid attributeValue ${catalogType.type}: ${attributeValue}`);
          }
          break;
        case CATALOG_ATTR_TYPES.BOOLEAN:
          attribute.int_value = (() => {
            switch (attributeValue) {
              case true:
                return 1;
              case false:
                return 0;
              default:
                throw new Error(`invalid attributeValue BOOLEAN: ${attributeValue}`);
            }
          })();
          break;
        case CATALOG_ATTR_TYPES.PERCENT:
          if (typeof attributeValue === 'number') {
            // eslint-disable-next-line unicorn/prefer-number-properties -- TODO: try switching to Number.isFinite
            if (!isFinite(attributeValue)) {
              throw new TypeError('attributeValue must be finite');
            }
            attributeValue = attributeValue.toString();
          }
          if (typeof attributeValue === 'string') {
            const tokens = attributeValue.match(/^(\d+)((\.)(\d+))?/);
            if (tokens) {
              const intPart = Number.parseInt(tokens[1], 10) * ONE_PERCENT;
              let fractPart;
              if (!isNone(tokens[4])) {
                fractPart = Math.trunc(Number.parseInt(tokens[4], 10) * ONE_PERCENT / Math.pow(10, tokens[4].length));
              } else {
                fractPart = 0;
              }
              attribute.int_value = intPart + fractPart;
            } else {
              throw new Error('attributeValue must be a valid float');
            }
          } else {
            throw new TypeError(`invalid attributeValue PERCENT: ${attributeValue}`);
          }
          break;
        case CATALOG_ATTR_TYPES.JSON:
          attribute.string_value = JSON.stringify(attributeValue);
          break;
        case CATALOG_ATTR_TYPES.STRING_AND_INT:
          attribute.string_value = attributeValue.string_value;
          attribute.int_value = attributeValue.int_value;
          break;
        default:
          throw new Error(`unhandled catalogType.type: ${catalogType.type}`);
      }
    }
    return attribute;
  },
  // serializes given attributes into catalogObject with optional unitToken
  _serializeCatalogAttributes(catalogObject, attributes, unitToken, removeItemsBulkAttributes = true) {
    for (const attributeName in attributes) {
      // Specially handled or ignored attribute names
      const attributeValue = attributes[attributeName];
      // for true only attributes, don't send the attribute if value is false.
      if (A(TRUE_ONLY_ATTRIBUTES).includes(attributeName) && !attributeValue) {
        continue;
      }
      if (A(CATALOG_SERVICE_META_ATTRIBUTES).includes(attributeName)) {
        continue;
      }
      // Only process null attribute values when dealing with unit overrides
      if (isNone(attributeValue) && isNone(unitToken)) {
        continue;
      }

      // items-bulk has logic that adds additional "attributes" to catalog objects before returning them to Dashboard.
      // items doesn't know about these attributes and will return an error if we send them. The items-bulk 'put'
      // endpoint will filter these before calling items, but 'process-batch' goes directly to items. However, we want to
      // retain these attribute when this function is used to mock data for tests, which we do with the removeItemsBulkAttributes flag.
      if (removeItemsBulkAttributes && ITEMS_BULK_FAKE_ATTRIBUTES.includes(attributeName)) {
        continue;
      }
      if (!isNone(CATALOG_SERVICE_ATTRIBUTE_DEFINITIONS[attributeName])) {
        const attributeDef = CATALOG_SERVICE_ATTRIBUTE_DEFINITIONS[attributeName];
        if (attributeDef.type === CATALOG_ATTR_TYPES.TOKEN_AND_INT) {
          attributeValue?.forEach((id, i) => catalogObject.attribute.push({
            definition_token: attributeDef.token,
            string_value: id,
            int_value: i
          }));
        } else if (this._isAttributeRepeatedOnObjectType(attributeDef, catalogObject.type)) {
          attributeValue.forEach(value => catalogObject.attribute.push(this._serializeCatalogAttribute(attributeName, value, unitToken)));
        } else {
          catalogObject.attribute.push(this._serializeCatalogAttribute(attributeName, attributeValue, unitToken));
        }
      } else {
        // We currently have a test failing because it has an attribute of invalid set to true
        // which is failing the isNone check above. We should not try to iterate through this value
        if (attributeName === 'invalid') {
          continue;
        }

        // Handle composite attributes like 'price'
        for (const subAttrName in attributeValue) {
          if (subAttrName === '_super') {
            // '_super' breaks serialization. It's not even supposed to be iterated over.
            continue;
          }
          const subAttrValue = attributeValue[subAttrName];
          const fullName = `${attributeName}.${subAttrName}`;
          const attributeDef = CATALOG_SERVICE_ATTRIBUTE_DEFINITIONS[fullName];
          if (this._isAttributeRepeatedOnObjectType(attributeDef, catalogObject.type)) {
            subAttrValue.forEach(subValue => catalogObject.attribute.push(this._serializeCatalogAttribute(fullName, subValue, unitToken)));
          } else {
            catalogObject.attribute.push(this._serializeCatalogAttribute(fullName, subAttrValue, unitToken));
          }
        }
      }
    }
  },
  _toCatalogObject(object, removeStockCount = true) {
    const catalogObject = {};
    catalogObject.type = CATALOG_SERVICE_TYPES[object.catalog_type];
    if (object.id) {
      catalogObject.token = object.id;
    }
    catalogObject.attribute = [];
    catalogObject.version = object.version;
    if (object.deleted) {
      catalogObject.deleted = object.deleted;
    }
    catalogObject.attribute.push(this._serializeCatalogAttribute('unit_enabled', Boolean(object.enabled_units_all)));
    if (isPresent(object.enabled_units)) {
      object.enabled_units.forEach(unit => {
        catalogObject.attribute.push(this._serializeCatalogAttribute('unit_enabled', true, unit.token));
      });
    }
    if (isPresent(object.disabled_units)) {
      object.disabled_units.forEach(unit => {
        catalogObject.attribute.push(this._serializeCatalogAttribute('unit_enabled', false, unit.token));
      });
    }
    for (const unitToken in object.unit_overrides) {
      const unitOverrideAttributes = object.unit_overrides[unitToken];
      this._serializeCatalogAttributes(catalogObject, unitOverrideAttributes, unitToken, removeStockCount);
    }
    const recognizedCustomAttributeDefinitionTokens = new Set(object.custom_attributes?.map(attribute => attribute.definition_token));
    object.unknown_attrs?.forEach(unknownAttributeValue => {
      // prevent re-adding recognized custom attributes that exist in unknown attributes
      // custom attributes may exist in unknown_attributes if the model was loaded from a bulk interface
      // that doesn't include custom attribute definitions
      // an example of such an interface is the search endpoint
      // See https://block.atlassian.net/browse/ONCALL-153770 for an example issue.
      if (recognizedCustomAttributeDefinitionTokens.has(unknownAttributeValue.definition_token)) {
        return;
      }

      // send back unknown attributes as we received them.
      const int_value = typeof unknownAttributeValue.int_value === 'string' ? Number.parseInt(unknownAttributeValue.int_value, 10) : unknownAttributeValue.int_value;
      const attribute = {
        definition_token: unknownAttributeValue.definition_token,
        string_value: unknownAttributeValue.string_value,
        int_value,
        unit_token: unknownAttributeValue.unit_token
      };

      // TODO: Remove this code once server no longer sends values in a different format from
      // how it expects them
      if (!isNone(unknownAttributeValue.Value?.StringValue)) {
        attribute.string_value = unknownAttributeValue.Value.StringValue;
      }
      if (!isNone(unknownAttributeValue.Value?.IntValue)) {
        attribute.int_value = Number.parseInt(unknownAttributeValue.Value.IntValue, 10);
      }
      catalogObject.attribute.push(attribute);
    });
    this._serializeCatalogAttributes(catalogObject, object, undefined, removeStockCount);
    this._serializeItemOptionAttributes(catalogObject, object);
    this._serializeResourceAttributes(catalogObject, object);
    if (object.custom_attributes) {
      catalogObject.attribute.push(...object.custom_attributes);
    }
    return catalogObject;
  },
  _toCatalogResponseObject(object) {
    const catObj = this._toCatalogObject(object, false);
    return formatObjectAsCatalogResponse(catObj);
  },
  _catalogModelHasAttribute(modelName, attrName, attrType) {
    const model = this.store.modelFor(modelName);
    if (attrType === CATALOG_ATTR_TYPES.TOKEN) {
      const serializer = this.store.serializerFor(modelName);
      const belongsToKeys = model.relationshipNames.belongsTo.map(key => serializer.keyForRelationship(key, 'belongsTo'));
      const hasManyKeys = model.relationshipNames.hasMany.map(key => serializer.keyForRelationship(key, 'hasMany'));
      const relationshipKeys = new Set([...belongsToKeys, ...hasManyKeys]);
      return relationshipKeys.has(attrName);
    } else {
      // split name on '.', because attributes like "price.cents" will show up as "price" in the map
      return model.attributes.has(camelize(attrName.split('.')[0]));
    }
  },
  _fromCatalogObject(catalogObject, customAttributeDefinitionTokens = []) {
    const jsonObj = {
      id: catalogObject.token,
      version: catalogObject.version,
      enabled_units: [],
      disabled_units: [],
      enabled_units_all: true,
      unit_overrides: {},
      unknown_attrs: [],
      custom_attributes: [],
      item_option_id_map: {},
      item_option_value_id_map: {},
      resources: []
    };
    if (isPresent(catalogObject.deleted)) {
      jsonObj.deleted = catalogObject.deleted;
    }
    const uniqueCustomAttributeDefinitionTokens = new Set(customAttributeDefinitionTokens);
    const catalogType = this._getCatalogType(catalogObject);

    // NB(cclegg): The server will return info for objects that were updated or deleted as a side effect of updates and deletes performed via
    // the `process-batch` endpoint. It's possible that a change from Dashboard can cause a cascading change to an object type that we don't
    // have a model for on Dashboard. In that case we can just return the empty object from here.
    const catalogTypeModelName = this.store.serializerFor('items-v3-base')._modelNameForCatalogType(catalogType);
    if (!catalogTypeModelName) {
      return jsonObj;
    }
    const imageMembershipAttrDef = CATALOG_SERVICE_ATTRIBUTE_DEFINITIONS['image_memberships'];
    if (this._catalogModelHasAttribute(catalogTypeModelName, imageMembershipAttrDef.name, imageMembershipAttrDef.type)) {
      jsonObj.image_memberships = [];
    }

    // eslint-disable-next-line complexity
    (catalogObject.attribute || []).forEach(attr => {
      let attributeDef = CATALOG_SERVICE_TOKEN_TO_ATTRIBUTE_DEF_MAP[attr.definition_token];

      // the page tile model only has a single image and handles the relationship differently
      // so it needs to be deserialized differently.
      if (attr.definition_token === 'SQ_COGS_IMID') {
        if (catalogObject.type === 'PAGE_TILE') {
          attributeDef = CATALOG_SERVICE_ATTRIBUTE_DEFINITIONS['image_id'];
        } else {
          attributeDef = CATALOG_SERVICE_ATTRIBUTE_DEFINITIONS['image_memberships'];
        }
      }
      if (isNone(attributeDef)) {
        if (uniqueCustomAttributeDefinitionTokens.has(attr.definition_token)) {
          jsonObj.custom_attributes.push(attr);
        } else if (attr.definition_token === CATALOG_SERVICE_RESOURCE_TOKEN) {
          jsonObj.resources.push({
            resource_id: attr.Value?.StringValue ?? attr.string_value,
            unit_token: attr.unit_token
          });
        } else {
          jsonObj.unknown_attrs.push(attr);
        }
      } else if (this._catalogModelHasAttribute(catalogTypeModelName, attributeDef.name, attributeDef.type) || this._isAttributeRepeatedOnObjectType(attributeDef, catalogObject.type) || UNIT_OVERRIDE_ONLY_ATTRIBUTES.includes(attributeDef.name) || attributeDef.name === 'unit_enabled') {
        const value = deserializeCatalogAttributeValue(attributeDef.type, attr);
        if (attributeDef.name === 'unit_enabled') {
          if (value) {
            if (attr.unit_token) {
              jsonObj.enabled_units.push({
                token: attr.unit_token
              });
            } else {
              jsonObj.enabled_units_all = true;
            }
          } else {
            if (attr.unit_token) {
              jsonObj.disabled_units.push({
                token: attr.unit_token
              });
            } else {
              jsonObj.enabled_units_all = false;
            }
          }
        } else {
          let attrs = jsonObj;
          if (attr.unit_token) {
            if (!jsonObj.unit_overrides[attr.unit_token]) {
              jsonObj.unit_overrides[attr.unit_token] = {};
            }
            attrs = jsonObj.unit_overrides[attr.unit_token];
          }
          if (!attributeDef.name.match('\\.')) {
            if (this._isAttributeRepeatedOnObjectType(attributeDef, catalogObject.type)) {
              // handle repeated attributes
              if (!attrs[attributeDef.name]) {
                attrs[attributeDef.name] = [];
              }
              attrs[attributeDef.name].push(value);
            } else {
              attrs[attributeDef.name] = value;
            }
          } else {
            // Handle composite attributes like 'price'
            const [compositeName, subAttrName] = attributeDef.name.split('.');
            if (!attrs[compositeName]) {
              attrs[compositeName] = {};
            }
            if (this._isAttributeRepeatedOnObjectType(attributeDef, catalogObject.type)) {
              // handle repeated attributes
              if (!attrs[compositeName][subAttrName]) {
                attrs[compositeName][subAttrName] = [];
              }
              attrs[compositeName][subAttrName].push(value);
            } else {
              attrs[compositeName][subAttrName] = value;
            }
          }
        }
      } else if (attributeDef.type === CATALOG_ATTR_TYPES.ITEM_OPTION) {
        jsonObj.item_option_id_map[attributeDef.token] = deserializeCatalogAttributeValue(attributeDef.type, attr);
      } else if (attributeDef.type === CATALOG_ATTR_TYPES.ITEM_OPTION_VAL) {
        jsonObj.item_option_value_id_map[attributeDef.token] = deserializeCatalogAttributeValue(attributeDef.type, attr);
      } else {
        jsonObj.unknown_attrs.push(attr);
      }
    });
    jsonObj.catalog_type = catalogType;

    // item option and value id attributes could be in any order on the catalog object
    // we populate a map of definition => id above and convert it to a sorted array here
    jsonObj.item_option_ids = [];
    jsonObj.item_option_value_ids = [];
    CATALOG_SERVICE_ITEM_OPTION_ATTRIBUTE_TOKENS.forEach(token => {
      const itemOptionId = jsonObj.item_option_id_map[token];
      if (isPresent(itemOptionId)) {
        jsonObj.item_option_ids.push(itemOptionId);
      }
    });
    CATALOG_SERVICE_ITEM_OPTION_VAL_ATTRIBUTE_TOKENS.forEach(token => {
      const itemOptionValueId = jsonObj.item_option_value_id_map[token];
      if (isPresent(itemOptionValueId)) {
        jsonObj.item_option_value_ids.push(itemOptionValueId);
      }
    });
    jsonObj.custom_attributes.forEach(customAttribute => {
      if (customAttribute.Value) {
        for (const key in customAttribute.Value) {
          customAttribute[underscore(key)] = customAttribute.Value[key];
        }
        delete customAttribute.Value;
      }
    });
    delete jsonObj.item_option_id_map;
    delete jsonObj.item_option_value_id_map;
    Object.keys(jsonObj).forEach(attributeKey => {
      const attrDef = CATALOG_SERVICE_ATTRIBUTE_DEFINITIONS[attributeKey];
      if (attrDef?.type === CATALOG_ATTR_TYPES.TOKEN_AND_INT) {
        const values = jsonObj[attributeKey];
        values.sort((a, b) => a.int_value - b.int_value);
        jsonObj[attributeKey] = values.map(({
          string_value
        }) => string_value);
      }
    });
    return jsonObj;
  },
  _getCatalogAttribute(catalogObject, attrName) {
    return (catalogObject.attribute || []).find(attr => attr.definition_token === attrName);
  },
  _getCatalogType(catalogObject) {
    const typeAttr = this._getCatalogAttribute(catalogObject, 'SQ_10');
    const applicationMethodAttr = this._getCatalogAttribute(catalogObject, 'SQ_DAM');
    if (catalogObject.type === 'ITEM' && !isNone(typeAttr) && (typeAttr.Value?.IntValue === 1 || typeAttr.int_value === 1)) {
      return 'GIFT_CARD';
    } else if (catalogObject.type === 'DISCOUNT' && !isNone(applicationMethodAttr) && (applicationMethodAttr.Value?.IntValue === 1 || applicationMethodAttr.int_value === 1)) {
      return 'COMP_DISCOUNT';
    } else {
      return catalogObject.type;
    }
  },
  _getCatalogTypeFilterQuery(catalogType) {
    if (catalogType === 'ITEM') {
      const canUseCombos = (this.features.isEnabled('catalog/combos_r0') || this.features.isEnabled('catalog/release-combos-beta-2025')) && this.userSubscriptions.isSubscribedToCombos;
      let set_query_values = ItemTypesForFiltering_NumericStrings;
      if (canUseCombos) {
        // https://block.atlassian.net/browse/IF-5711
        // TODO: Once GA release of combos is completed, move COMBO to ItemTypesForFiltering in item-type-constants
        set_query_values = [...set_query_values, CatalogItemProductType.COMBO.toString()];
      }
      return {
        type: CATALOG_SERVICE_QUERY_TYPES.SET,
        key: 'SQ_10',
        set_query_values
      };
    } else if (catalogType === 'GIFT_CARD') {
      return {
        type: CATALOG_SERVICE_QUERY_TYPES.RANGE,
        key: 'SQ_10',
        range: {
          begin: 1,
          end: 1
        }
      };
    } else if (catalogType === 'COMP_DISCOUNT') {
      return {
        type: CATALOG_SERVICE_QUERY_TYPES.RANGE,
        key: 'SQ_DAM',
        range: {
          begin: 1,
          end: 1
        }
      };
    } else if (catalogType === 'APPOINTMENTS_SERVICE') {
      return {
        type: CATALOG_SERVICE_QUERY_TYPES.RANGE,
        key: 'SQ_10',
        range: {
          begin: 2,
          end: 2
        }
      };
    } else {
      return null;
    }

    /* If a retail goods model is added its filter would look like this:
    else if (catalogType === 'GOODS') {
      return { type: CATALOG_SERVICE_QUERY_TYPES.RANGE, key: 'SQ_10', range: { begin: 3, end: 3 } };
    } */
  },
  _getCatalogObjectsFromResponse(response, customAttributeDefinitionTokens = []) {
    let objects = [];
    if (isPresent(response.deleted_objects)) {
      objects = response.deleted_objects;
    } else if (isPresent(response.catalog_object)) {
      objects = response.catalog_object;
    }
    if (isEmpty(customAttributeDefinitionTokens) && isPresent(response.attribute_definition)) {
      customAttributeDefinitionTokens = response.attribute_definition.filter(definition => definition.attribute_class === 'CUSTOM').map(definition => definition.token);
    }
    return objects.map(catalogObject => this._fromCatalogObject(catalogObject, customAttributeDefinitionTokens));
  },
  _isAttributeRepeatedOnObjectType(attrDef, objectType) {
    const typeName = CATALOG_SERVICE_TYPES_INVERSE[objectType];
    if (isNone(attrDef) || isNone(attrDef.repeated)) {
      return null;
    } else if (isNone(typeName)) {
      return attrDef.repeated[objectType];
    } else {
      return attrDef.repeated[typeName];
    }
  },
  async cloneUnit(sourceUnitToken, targetUnitToken) {
    const params = {
      source_unit: sourceUnitToken,
      target_unit: targetUnitToken,
      use_async: true
    };
    const {
      status_token
    } = await jsonPostWithErrorWrapper(URL_CLONE_UNIT, params);
    if (status_token) {
      await this.waitForAsyncService(status_token);
    }
  },
  async waitForAsyncService(statusToken) {
    // eslint-disable-next-line unicorn/no-this-assignment
    const catalogService = this;
    let finished = false;
    while (!finished) {
      finished = await new Promise(function (resolve, reject) {
        later(this, async function () {
          const response = await catalogService.getAsyncServiceStatus(statusToken);
          const statusCode = ASYNC_SERVICE_STATUS_CODES[response.status_code];
          if (statusCode === ASYNC_SERVICE_STATUS_CODES.SUCCESS) {
            resolve(true);
          } else if (statusCode === ASYNC_SERVICE_STATUS_CODES.IN_PROGRESS) {
            resolve(false);
          } else {
            reject(new Error('Async service finished with non-success status.'));
          }
        }, 1000);
      });
    }
  },
  batch({
    putObjects = [],
    deleteObjects = []
  }, options) {
    const isPut = !isEmpty(putObjects);
    const isDelete = !isEmpty(deleteObjects);
    const params = {
      request: [{
        put_request: this._buildPutRequest(putObjects, options)
      }, {
        delete_request: this._buildDeleteRequestFromTokens(deleteObjects.map(d => d.id))
      }]
    };
    return jsonPostWithErrorWrapper(URL_BATCH, params).then(response => {
      const expectedPutResponseLength = putObjects.length;
      const putResponseError = errorForProcessBatchPost(response, expectedPutResponseLength);
      if (isPresent(putResponseError)) {
        Logger.error('CatalogService.batch error', putResponseError);
        throw putResponseError;
      }
      let putRecords = [];
      if (isPut) {
        const putResponse = response.response[0].put_response;
        this._updateCatalogVersion(putResponse.modification_timestamp);
        putRecords = this._getCatalogObjectsFromResponse(putResponse);
      }
      let deleteRecords = [];
      if (isDelete) {
        if (isNone(response.response[1])) {
          const error = new CatalogError({
            catalogErrorType: CatalogErrorType.RPC_ERROR,
            message: 'Delete objects were present in the request but none found in response'
          });
          Logger.error('CatalogService.batch error', putResponseError);
          throw error;
        }
        deleteRecords = this._getCatalogObjectsFromResponse(response.response[1].delete_response);
      }
      return {
        putRecords,
        deleteRecords
      };
    });
  }
});

// This function parses the PutResponse and returns a CatalogError if it finds an
// error or unmet invariant (see CatalogErrorType enum for the distinct types of
// errors that can be returned), or undefined if the response was successful. Consumers
// shouldn't need to use this directly but it is exported for testing.
// See https://prototype.sqprod.co/#/docs/squareup.items.merchant.PutResponse
export function errorForProcessBatchPost(response, expectedPutResponseLength) {
  if (isNone(response.response[0])) {
    return new CatalogError({
      catalogErrorType: CatalogErrorType.RPC_ERROR,
      message: "Missing 'PutResponse' in batch response"
    });
  }

  // NB(jlintern): the DeleteResponse proto doesn't have an error field
  const putResponse = response.response[0].put_response;
  if (isEmpty(putResponse.status)) {
    return new CatalogError({
      catalogErrorType: CatalogErrorType.RPC_ERROR,
      message: "PutResponse missing 'status' field"
    });
  }
  if (putResponse.status === 'ERROR') {
    if (!isArray(putResponse.invalid_catalog_object) || isEmpty(putResponse.invalid_catalog_object)) {
      return new CatalogError({
        catalogErrorType: CatalogErrorType.RPC_ERROR,
        message: 'PutResponse: status is ERROR but invalid_catalog_object[] is missing or empty'
      });
    }
    const firstInvalidCatalogObjectError = putResponse.invalid_catalog_object.map(invalidCatalogObject => {
      if (isPresent(invalidCatalogObject.reason)) {
        let catalogErrorType = CatalogErrorType.BAD_REQUEST;
        switch (invalidCatalogObject.reason) {
          case 'VERSION_MISMATCH':
            catalogErrorType = CatalogErrorType.VERSION_MISMATCH;
            break;
          case 'TOO_MANY_ATTRIBUTES':
            catalogErrorType = CatalogErrorType.TOO_MANY_ATTRIBUTES;
            break;
          default:
            break;
        }
        return new CatalogError({
          catalogErrorType,
          message: `object with token ${invalidCatalogObject.catalog_object.token} ` + `is invalid. Reason: ${invalidCatalogObject.reason};`,
          badObject: invalidCatalogObject.catalog_object
        });
      }
      return undefined;
    }).find(error => isPresent(error));
    if (isPresent(firstInvalidCatalogObjectError)) {
      return firstInvalidCatalogObjectError;
    } else {
      return new CatalogError({
        catalogErrorType: CatalogErrorType.BAD_REQUEST,
        message: 'unknown error'
      });
    }
  } else {
    // putResponse.status === "SUCCESS"
    if (expectedPutResponseLength !== putResponse.catalog_object.length) {
      return new CatalogError({
        catalogErrorType: CatalogErrorType.RPC_ERROR,
        message: 'CatalogService.batch: put_response.catalog_object.length is ' + `${putResponse.catalog_object.length}, expected ${expectedPutResponseLength}`
      });
    }
    return undefined;
  }
}
function jsonPostWithErrorWrapper(url, params) {
  return jsonPost(url, params).catch(error => {
    const catalogError = CatalogError.fromXhrError(error);
    sendErrorToSentry(catalogError);
    switch (catalogError.catalogErrorType) {
      case CatalogErrorType.NOT_FOUND:
        Logger.error(`${url} returned a 404 NOT-FOUND. If this happens in test code, make sure to setup the appropriate test helpers. If this happens in staging or prod something is horribly wrong.`, catalogError);
        break;
      default:
        Logger.error(`CatalogService error calling ${url}`, catalogError);
        break;
    }
    throw catalogError;
  });
}
function uploadMultipartWithErrorWrapper(...args) {
  return uploadMultipart(...args).catch(error => {
    const catalogError = CatalogError.fromXhrError(error);
    sendErrorToSentry(catalogError);
    throw error;
  });
}